import React, { useState, useEffect, useMemo, useCallback } from "react";

import { useTranslation } from "react-i18next";

import { IoMdArrowBack } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { RiInformation2Fill } from "react-icons/ri";

import { useLoading } from "../../context/LoadingContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import config from '../../config';
import FloatingMenu from "../FloatingMenu";
import ErrorBanner from "../ui/ErrorBanner";

import TransactionStatus from "./TransactionStatus";

import {
    formatCurrency,
    sanitizeNumberInput,
    preventInvalidKeyInput
} from "../../utils/global";

import {
    fetchBalance,
    fetchBankList,
    fetchDepositBankDetailList,
    fetchPaymentGatewayList,
    fetchPaymentGatewayStatus,
    fetchUserBankList,
    updateUserBankDetails,
    sendInvoice,
} from "../../utils/api";
import { assetPath } from "../../utils/assets";

const PaymentGatewayWithdraw = () => {
    const { t } = useTranslation();
    const { setLoading } = useLoading();
    const { token, phoneNumber } = useAuth();
    const navigate = useNavigate();

    const [error, setError] = useState({
        message: "",
        shake: false
    });
    const [wallet, setWallet] = useState({
        balance: 0,
        status: null
    });
    const [bank, setBank] = useState({
        list: [],
        details: [],
        selected: null
    });
    const [user, setUser] = useState({
        bank: null,
        method: null,
        selected: null,
        accountName: null,
        accountNumber: null
    });
    const [withdrawal, setWithdrawal] = useState({
        amount: "",
        display: "",
        options: ["10000", "20000", "50000", "100000", "200000", "500000"]
    });
    const [paymentGateway, setPaymentGateway] = useState(null);
    const [gatewayRequest, setGatewayRequest] = useState({
        sourceId: null,
        status: null,
    });

    const clearError = useCallback(() => {
        setError(prev => ({...prev, message: "" }));
    }, []);

    const bankImageMap = {
        19: assetPath("/icons/bca.png"),
        20: assetPath("/icons/mandiri.png"),
        21: assetPath("/icons/bri.png"),
        22: assetPath("/icons/bni.png"),
    };

    const generateTransferUniqueId = useCallback(() => {
        const timestamp = Date.now().toString(36);
        const randomNum = Math.random().toString(36).substring(2, 7);
        return `${timestamp}${randomNum}`;
    }, []);

    const getTransferSourceId = useCallback(() => {
        const playerName = String(phoneNumber || "player").replace(/[^a-zA-Z0-9]/g, "");
        return `${playerName}-${generateTransferUniqueId()}`;
    }, [phoneNumber, generateTransferUniqueId]);

    const getPaymentOptionMeta = useCallback((gateway) => {
        switch (gateway.channel_name) {
            case "QRpay":
                return {
                    paymentMethod: "QR",
                    bankCode: "DANAQR",
                    icon: assetPath("/icons/QRIS.png"),
                };
            case "QRis":
                return {
                    paymentMethod: "QR",
                    bankCode: "QRIS",
                    icon: assetPath("/icons/QRIS.png"),
                };
            default:
                return {
                    paymentMethod: "BANK",
                    bankCode: gateway.payment_name || gateway.name,
                    icon: assetPath("/icons/bank transfer.png"),
                };
        }
    }, []);

    const selectedDetail = bank.selected
        ? bank.details.find((detail) => detail.bank_id === bank.selected.bank_id)
        : null;

    const userBanks = useMemo(() => {
        const rawBanks = Array.isArray(user.bank)
            ? user.bank
            : user.bank
                ? [user.bank]
                : [];

        return rawBanks.map((b) => ({
            ...b,
            bankMeta: bank.list.find((item) => item.id === b.bank_id) || null,
        }));
    }, [user.bank, bank.list]);

    const selectedBankMeta = bank.selected?.bankMeta || null;

    const checkMinMaxWithdraw = useCallback((amount) => {
        if (isNaN(amount)) {
            clearError();
            return;
        }

        const minWithdraw = selectedDetail?.min_deposit?? 10000;
        const maxWithdraw = selectedDetail?.max_deposit?? 10000000;

        let message = "";
        if (amount < minWithdraw) {
            message = t("minWithdrawal", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(minWithdraw, "currency", config.CURRENCY_ISO_CODE) });
        } else if (amount > maxWithdraw) {
            message = t("maxWithdrawal", { currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(maxWithdraw, "currency", config.CURRENCY_ISO_CODE) });
        } else if (amount > wallet.balance) {
            message = t("insufficientFunds");
        }

        setError(prev => prev.message === message ? prev : { ...prev, message, shake: !!message });

        if (message) {
            setBank((prev) => ({ ...prev, selected: null, confirmed: null }));
            setTimeout(() => setError(prev => ({ ...prev, shake: false })), 500);
        }
    }, [t, clearError, wallet.balance, selectedDetail]);

    const handleWithdrawAmountChange = useCallback((input) => {
        const inputValue = input?.target ? input.target.value : input;
        const sanitizedValue = sanitizeNumberInput(String(inputValue ?? ""));

        if (!sanitizedValue) {
            setWithdrawal(prev => ({ ...prev, amount: "", display: "" }));
            clearError();
            return;
        }

        setWithdrawal(prev => ({
            ...prev,
            amount: sanitizedValue,
            display: formatCurrency(sanitizedValue, "currency", config.CURRENCY_ISO_CODE),
        }));

        const amount = Number(sanitizedValue);
        checkMinMaxWithdraw(amount);
    }, [checkMinMaxWithdraw, clearError]);

    const loadData = useCallback(async () => {
        setLoading(true);
        clearError();

        try {
            const [balanceResponse, userBankResponse, banksResponse, banksDetailsResponse, paymentGatewayResponse] = await Promise.all([
                fetchBalance(),
                fetchUserBankList(),
                fetchBankList(),
                fetchDepositBankDetailList(),
                fetchPaymentGatewayList(),
            ]);
            const activePaymentGateways = paymentGatewayResponse.success && Array.isArray(paymentGatewayResponse.data)
                ? paymentGatewayResponse.data.filter((gateway) => Number(gateway.status) === 1)
                : [];
            const paymentOptions = activePaymentGateways.map((gateway) => ({
                ...gateway,
                ...getPaymentOptionMeta(gateway),
                name: gateway.payment_name || gateway.name,
                paymentGatewayId: gateway.id,
            }));
            const bankGateway = paymentOptions.find((gateway) => gateway.channel_name === "Payment Gateway") || null;

            setBank(prev => ({
                ...prev,
                list: banksResponse.success && Array.isArray(banksResponse.data) ? banksResponse.data : [],
                details: banksDetailsResponse.success && Array.isArray(banksDetailsResponse.data) ? banksDetailsResponse.data : []
            }));
            setPaymentGateway(bankGateway);

            setWallet(prev => ({
                ...prev,
                balance: balanceResponse.success ? balanceResponse.data : 0,
            }));

            setUser(prev => ({
                ...prev,
                bank: userBankResponse.success && userBankResponse.data
                    ? (Array.isArray(userBankResponse.data) ? userBankResponse.data : [userBankResponse.data])
                    : []
            }));
        } catch (error) {
            setError(prev => ({ ...prev, message: t("pleaseTryLater") }));
        } finally {
            setLoading(false);
        }
    }, [t, setLoading, clearError, getPaymentOptionMeta]);

    const refreshWalletBalance = useCallback(async () => {
        const balanceResponse = await fetchBalance();

        if (balanceResponse.success) {
            setWallet(prev => ({
                ...prev,
                balance: balanceResponse.data,
            }));
        }

        window.dispatchEvent(new Event("wallet:refresh"));
    }, []);

    const handleApi = useCallback(async (params) => {
        setLoading(true);
        clearError();

        try {
            const response = await sendInvoice(params);

            if (response.success) {
                await refreshWalletBalance();
                setGatewayRequest({
                    sourceId: params.sourceId,
                    status: "pending",
                });

                setWallet(prev => ({
                    ...prev,
                    status: {
                        status: "success",
                        method: "Payment Gateway",
                        title: t("submitSuccess"),
                        message: t("requestBeingProcessed"),
                        onPrimaryAction: () => navigate("/"),
                        primaryTitle: t("backToHome"),
                    },
                }));
            } else {
                const apiError = response.message || response.data;
                if (typeof apiError === "string") {
                    let msg = apiError;
                    if (apiError.toLowerCase().includes("promotion turnover target")) {
                        const match = apiError.match(/target ([\d.]+)/);
                        msg = t("promotionTurnoverError", { amount: formatCurrency(match ? match[1] : "unknown") });
                    } else if (apiError.toLowerCase().includes("turnover target")) {
                        const match = apiError.match(/target ([\d.]+)/);
                        msg = t("turnoverTargetError", { amount: formatCurrency(match ? match[1] : "unknown") });
                    } else if (apiError.toLowerCase().includes("minimum withdrawal")) {
                        const match = apiError.match(/is ([\d.]+)/);
                        msg = t("minWithdrawalError", { amount: formatCurrency(match ? match[1] : "unknown") });
                    }

                    setError({ message: msg, shake: true });
                    setTimeout(() => setError(prev => ({...prev, shake: false })), 500);
                } else {
                    setWallet(prev => ({
                        ...prev,
                        status: {
                            method: "Payment Gateway",
                            title: t("encounteredError"),
                            message: t("unknownError"),
                            onPrimaryAction: () => {window.location.reload()},
                            primaryTitle: t("tryAgain"),
                            onSecondaryAction: () => {
                                if (window.Telegram?.WebApp) {
                                    window.Telegram.WebApp.openTelegramLink(config.TELEGRAM_URL);
                                } else {
                                    window.open(config.TELEGRAM_URL, "_blank");
                                }
                            },
                            secondaryTitle: t("contactUsForSupport"),
                        },
                    }));
                }
            }
        } catch (err) {
            setError(prev => ({ ...prev, message: t("failedToFetchLink") }));
        } finally {
            setLoading(false);
        }
    }, [t, navigate, setLoading, clearError, refreshWalletBalance]);

    const handleSubmit = useCallback(() => {
        if (!withdrawal.amount || !bank.selected?.account_holder || !bank.selected?.account_no || !paymentGateway?.id) {
            setError({ message: t("pleaseTryLater"), shake: true });
            setTimeout(() => setError(prev => ({...prev, shake: false })), 500);
            return;
        }

        const currentParams = {
            mcode: config.PAYMENT_GATEWAY_MCODE,
            sourceId: getTransferSourceId(),
            amount: withdrawal.amount,
            bankCode: bank.selected?.bankMeta?.name,
            paymentGatewayId: paymentGateway.id,
            accountHolderName: bank.selected.account_holder,
            accountNumber: bank.selected.account_no,
            currenciesSupported: config.CURRENCY_ISO_CODE,
            paymentMethod: paymentGateway.paymentMethod,
            type: "withdraw",
        };
        handleApi(currentParams);
    }, [handleApi, withdrawal.amount, bank.selected, paymentGateway, getTransferSourceId, t]);

    const handleUpdateUserBank = useCallback(async () => {
        setLoading(true);
        clearError();

        try {
            const response = await updateUserBankDetails({
                user_bank: {accountNumber : user.accountNumber, accountName : user.accountName },
                bank: user.selected,
            });

            if (response.success) {
                setWallet(prev => ({
                    ...prev,
                    status: {
                        status: "success",
                        method: "Payment Gateway",
                        title: t("submitSuccess"),
                        message: t("bankDetailsSuccessfullyUpdated"),
                        onPrimaryAction: () => {
                            setWallet(prev => ({ ...prev, status: null }));
                            setWithdrawal(prev => ({ ...prev, amount: "" }));
                            setUser(prev => ({ ...prev, method: null, selected: null }));
                            loadData();
                        },
                        primaryTitle: t("Withdraw"),
                        onSecondaryAction: () => navigate("/"),
                        secondaryTitle: t("backToHome"),
                    },
                }));
            } else {
                setWallet(prev => ({
                    ...prev,
                    status: {
                        method: "Payment Gateway",
                        title: t("encounteredError"),
                        message: t("unknownError"),
                        onPrimaryAction: () => {window.location.reload()},
                        primaryTitle: t("tryAgain"),
                        onSecondaryAction: () => {
                            if (window.Telegram?.WebApp) {
                                window.Telegram.WebApp.openTelegramLink(config.TELEGRAM_URL);
                            } else {
                                window.open(config.TELEGRAM_URL, "_blank");
                            }
                        },
                        secondaryTitle: t("contactUsForSupport"),
                    },
                }));
            }
        } catch (err) {
            setError(prev => ({ ...prev, message: t("failedToUpdateBankDetails") }));
        } finally {
            setLoading(false);
        }
    }, [t, navigate, setLoading, user.accountNumber, user.accountName, user.selected, clearError, loadData]);

    const fallbackCopyTextToClipboard = useCallback((text, msg) => {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();

        try {
            document.execCommand("copy");
            alert(msg);
        } catch (err) {
            console.error("Fallback copy failed:", err);
        }

        document.body.removeChild(textArea);
    }, []);

    const handleCopyId = useCallback((id, msg) => {
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard
                .writeText(id)
                .then(() => alert(msg))
                .catch((err) => {
                    console.error("Clipboard API failed, using fallback:", err);
                    fallbackCopyTextToClipboard(id, msg);
                });
        } else {
            fallbackCopyTextToClipboard(id, msg);
        }
    }, [fallbackCopyTextToClipboard]);
    
    useEffect(() => {
        if (error.message) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, [error.message]);

    useEffect(() => {
        if (token) {
            loadData();
        }
    }, [token, loadData]);

    useEffect(() => {
        const sourceId = gatewayRequest.sourceId;

        if (!sourceId || gatewayRequest.status !== "pending") {
            return;
        }

        let isMounted = true;
        let isChecking = false;

        const normalizeStatus = (status) => String(status || "").toLowerCase();
        const isSuccessStatus = (status) => ["success", "completed", "approved", "processed"].includes(status);
        const isFailedStatus = (status) => ["failed", "fail", "rejected", "error"].includes(status);
        const isExpiredStatus = (status) => ["expired", "timeout"].includes(status);

        const checkStatus = async () => {
            if (isChecking) return;
            isChecking = true;

            try {
                const response = await fetchPaymentGatewayStatus(sourceId);
                const status = normalizeStatus(response?.data?.status);

                if (!isMounted || !response?.success) {
                    return;
                }

                if (isSuccessStatus(status) || isFailedStatus(status) || isExpiredStatus(status)) {
                    setGatewayRequest(prev => ({ ...prev, status }));
                    await refreshWalletBalance();
                }
            } catch (error) {
                // Keep polling if a transient status check fails.
            } finally {
                isChecking = false;
            }
        };

        checkStatus();
        const intervalId = setInterval(checkStatus, 4000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [gatewayRequest.sourceId, gatewayRequest.status, refreshWalletBalance]);

    return (
        <section className="page-shell">
            <div className="page-header">
                <div
                    className="cursor-pointer bg-secondary container-back-button text-color-blue rounded-full"
                    onClick={() => navigate("/withdraw")}
                >
                    <IoMdArrowBack className="size-sm" />
                    <span className="text-sm">
                        {t("backToLobby")}
                    </span>
                </div>
                <button className="cursor-pointer" onClick={() => navigate("/")}>
                    <IoClose className="size-lg text-color-blue" />
                </button>
            </div>

            <ErrorBanner message={error.message} />

            <div className="px-4 pt-[30px] pb-[100px]">
                {wallet.status && <TransactionStatus {...wallet.status} />}
                {!wallet.status && (
                    <>
                        {userBanks.length > 0 && (
                            <>
                                <p className="mb-3 text-sm text-center font-bold text-white">
                                    {t("enterWithdrawAmount")}
                                </p>

                                <div className="info-banner mb-8">
                                    <RiInformation2Fill className="size-[30px] text-border-yellow" />
                                    <div className="flex flex-col flex-grow">
                                        <span className="text-xs text-white">
                                            {t("minWithdrawal", {currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(bank?.selected?.min_deposit ?? 10000, "currency", config.CURRENCY_ISO_CODE)})}
                                        </span>
                                        <span className="text-xs text-white">
                                            {t("maxWithdrawal", {currency: config.CURRENCY_ISO_CODE, amount: formatCurrency(bank?.selected?.max_deposit ?? 10000000, "currency", config.CURRENCY_ISO_CODE)})}
                                        </span>
                                    </div>
                                </div>

                                <div className="max-w-md my-6 text-center mx-auto w-fit">
                                    <div
                                        className={`text-[36px] font-bold text-white transition-transform duration-300 ${
                                            error.shake ? "animate-shake" : ""
                                        }`}
                                    >
                                        
                                        <span className="text-white/70 pl-1">
                                            {config.CURRENCY_ISO_CODE}
                                        </span>

                                        <input
                                            type="text"
                                            value={withdrawal.display || ""}
                                            onKeyDown={preventInvalidKeyInput}
                                            onChange={handleWithdrawAmountChange}
                                            placeholder="0"
                                            className="bg-transparent text-white text-[36px] font-bold text-right outline-none pr-2 w-auto min-w-[2ch] max-w-[10ch] md:max-w-[20ch]"
                                            style={{ width: `${(withdrawal.amount?.length || 1) + 1}ch` }}
                                        />
                                    </div>
                                </div>

                                <div className="flex flex-col text-center mb-8">
                                    <span className="text-sm font-bold text-white">
                                        {t("availableBalance")} :
                                    </span>
                                    <span className="font-bold text-color-second-light-blue">
                                        {`${config.CURRENCY_ISO_CODE} ${formatCurrency(wallet.balance, config.CURRENCY_ISO_CODE)}`}
                                    </span>
                                </div>

                                <div className="grid grid-cols-3 gap-3 my-8">
                                    {withdrawal.options.map((amount, index) => (
                                        <div
                                            key={index}
                                            className="relative"
                                        >
                                            {amount === "50000" && (
                                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-2 py-1 text-xxs font-bold text-black uppercase rounded-full btn-gradient w-max">
                                                    {t("popular")}
                                                </div>
                                            )}
                                            <div
                                                onClick={() => {
                                                    handleWithdrawAmountChange(amount);
                                                }}
                                                className={`cursor-pointer p-2 h-[36px] text-center transition-colors rounded-sm ${amount === `${withdrawal.amount}` ? "btn-gradient text-black" : "bg-main-gradient text-white"}`}
                                            >
                                                <span className="text-sm font-bold">
                                                    {formatCurrency(amount, "currency", config.CURRENCY_ISO_CODE)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div
                                    className={`transition-all duration-300 ease-in-out ${
                                        !error.message && withdrawal.amount 
                                            ? "opacity-100 translate-y-0 scale-100"
                                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                                    }`}
                                >
                                    <div className="mb-8">
                                        <p className="mb-3 text-sm font-bold text-white">
                                            {t("chooseWithdrawalMethod")}
                                        </p>

                                        <div className="rounded-lg bg-option-blue">
                                            {paymentGateway && [paymentGateway].map((gateway) => (
                                                <div
                                                    key={gateway.id}
                                                    className={`container-xs justify-start gap-2 cursor-pointer text-white 
                                                        border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0
                                                        ${user?.method === gateway.id ? "bg-option-second-selected-blue" : ""}`}
                                                    onClick={() => {
                                                        setUser((prev) => ({ ...prev, method: prev.method === gateway.id ? null : gateway.id }));
                                                        clearError();
                                                    }}
                                                >
                                                    <img 
                                                        src={gateway.icon} 
                                                        alt={gateway.name} 
                                                        className="size-lg object-contain"
                                                    />
                                                    
                                                    <span className="text-sm">
                                                        {gateway.name}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className={`transition-all duration-300 ease-in-out ${
                                        !error.message && withdrawal.amount && user.method
                                            ? "opacity-100 translate-y-0 scale-100"
                                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                                    }`}
                                >
                                    <div className="mb-8">
                                        <p className="mb-3 text-sm font-bold text-white">
                                            {t("selectWithdrawalBank")}
                                        </p>

                                        <div className="rounded-lg bg-main-gradient transition-all duration-300 ease-in-out">
                                            {selectedDetail ? (
                                                <>
                                                    <div
                                                        className="container-equal-lg justify-start gap-3 cursor-pointer text-white 
                                                            border-b border-option-selected-blue 
                                                            first:rounded-t-lg 
                                                            last:rounded-b-lg 
                                                            last:border-b-0
                                                            bg-option-second-selected-blue"
                                                        onClick={() => {
                                                            setBank((prev) => ({ ...prev, selected: null }));
                                                        }}
                                                    >
                                                        <img 
                                                            src={bankImageMap[bank.selected.bank_id]} 
                                                            alt={selectedBankMeta?.name || bank.selected.account_holder} 
                                                            className="size-xl"
                                                        />
                                                        <span className="text-sm">
                                                            {selectedBankMeta?.name || bank.selected.account_holder}
                                                        </span>
                                                    </div>

                                                    <div className="flex flex-col p-4 animate-fadeIn">
                                                        <p className="mb-3 text-sm font-bold text-white">
                                                            {t("bankDetails")}:
                                                        </p>
                                                        <div className="p-3 mb-3 bg-secondary rounded-lg flex flex-col">
                                                            <span className="text-sm text-color-blue">
                                                                {t("bankName")}:
                                                            </span> 
                                                            <span className="text-sm text-white">
                                                                {selectedBankMeta?.name || bank.selected.account_holder}
                                                            </span>
                                                        </div>
                                                        <div className="p-3 mb-3 bg-secondary rounded-lg flex-between">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm text-color-blue">
                                                                    {t("bankAccountNumber")}:
                                                                </span> 
                                                                <span className="text-sm text-white">
                                                                    {selectedDetail.account_number}
                                                                </span>
                                                            </div>
                                                            <div 
                                                                className="flex-center"
                                                                onClick={() => selectedDetail.account_number && handleCopyId(selectedDetail.account_number, t("accountNumberCopied", { accountNumber: selectedDetail.account_number }))}
                                                            >
                                                                <img
                                                                    src="/icons/copy1.png"
                                                                    alt="Copy"
                                                                    className="size-md"
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="p-3 mb-3 bg-secondary rounded-lg flex-between">
                                                            <div className="flex flex-col">
                                                                <span className="text-sm text-color-blue">
                                                                    {t("bankAccountName")}:
                                                                </span> 
                                                                <span className="text-sm text-white">
                                                                    {selectedDetail.account_name}
                                                                </span>
                                                            </div>
                                                            <div 
                                                                className="flex-center"
                                                                onClick={() => selectedDetail.account_name && handleCopyId(selectedDetail.account_name, t("accountNameCopied", { accountName: selectedDetail.account_name }))}
                                                            >
                                                                <img
                                                                    src="/icons/copy1.png"
                                                                    alt="Copy"
                                                                    className="size-md"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="overflow-hidden">
                                                    {userBanks.map((b) => (
                                                        <div
                                                            key={b.id}
                                                            className={`container-equal-lg cursor-pointer text-white border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0 gap-3 justify-start ${bank.selected?.id === b.id ? "bg-option-second-selected-blue" : ""}`}
                                                            onClick={() => {
                                                                setBank((prev) => ({ ...prev, selected: b }));
                                                                clearError();
                                                            }}
                                                        >
                                                            <img 
                                                                src={bankImageMap[b.bank_id]} 
                                                                alt={b.bankMeta?.name || b.account_holder} 
                                                                className="size-lg object-contain"
                                                            />
                                                            <span className="text-sm">
                                                                {b.bankMeta?.name || b.account_holder}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                        
                        {!userBanks.length > 0 && (
                            <>
                                <p className="mb-[20px] font-bold text-sm text-white text-center">
                                    {t("withdrawalBankDetails")}
                                </p>

                                <div
                                    className={`transition-all duration-300 ease-in-out ${
                                        !error.message
                                            ? "opacity-100 translate-y-0 scale-100"
                                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                                    }`}
                                >
                                    <div className="mb-8">
                                        <p className="mb-3 text-sm font-bold text-white">
                                            {t("chooseWithdrawalMethod")}
                                        </p>

                                        <div className="rounded-lg bg-option-blue">
                                            {paymentGateway && [paymentGateway].map((gateway) => (
                                                <div
                                                    key={gateway.id}
                                                    className={`container-xs justify-start gap-2 cursor-pointer text-white 
                                                        border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0
                                                        ${user?.method === gateway.id ? "bg-option-second-selected-blue" : ""}`}
                                                    onClick={() => {
                                                        setUser((prev) => ({ ...prev, method: prev.method === gateway.id ? null : gateway.id }));
                                                        clearError();
                                                    }}
                                                >
                                                    <img 
                                                        src={gateway.icon} 
                                                        alt={gateway.name} 
                                                        className="size-lg object-contain"
                                                    />
                                                    
                                                    <span className="text-sm">
                                                        {gateway.name}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                
                                <div
                                    className={`transition-all duration-300 ease-in-out ${
                                        !error.message && user.method
                                            ? "opacity-100 translate-y-0 scale-100"
                                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                                    }`}
                                >
                                    <div className="mb-8">
                                        <p className="mb-3 text-sm font-bold text-white">
                                            {t("selectWithdrawalBank")}
                                        </p>

                                        <div className="rounded-lg bg-option-blue">
                                            {bank.list.map((b) => (
                                                <div
                                                    key={b.id}
                                                    className={`container-equal-lg cursor-pointer text-white border-b border-option-selected-blue first:rounded-t-lg last:rounded-b-lg last:border-b-0 gap-3 justify-start ${
                                                        user.selected?.id === b.id ? "bg-option-second-selected-blue" : ""
                                                    }`}
                                                    onClick={() => {
                                                        setUser((prev) => ({ ...prev, selected: prev.selected?.id === b.id ? null : b }));
                                                        clearError();
                                                    }}
                                                >
                                                    <img
                                                        src={bankImageMap[b.id]}
                                                        alt={b.name}
                                                        className="size-lg object-contain"
                                                    />
                                                    <span className="text-sm">
                                                        {b.name}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className={`transition-all duration-300 ease-in-out ${
                                        !error.message && user.selected
                                            ? "opacity-100 translate-y-0 scale-100"
                                            : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                                    }`}
                                >
                                    <div className="mb-8">
                                        <p className="mb-3 text-sm font-bold text-white">
                                            {t("yourBankDetails")}
                                        </p>

                                        <div className="rounded-lg bg-main-gradient">
                                            <div className="flex flex-col p-4 animate-fadeIn">
                                                <div className="p-3 mb-3 bg-secondary rounded-lg flex flex-col">
                                                    <span className="text-sm text-color-blue">
                                                        {t("bankName")}:
                                                    </span> 
                                                    <span className="text-sm text-white">
                                                        {user?.selected?.name}
                                                    </span>
                                                </div>

                                                <p className="mb-3 text-sm font-bold text-white">
                                                    {t("EnterYourBankAccountNumber")}:
                                                </p>

                                                <div className="p-3 mb-3 bg-secondary rounded-lg flex-between">
                                                    <div className="flex flex-col">
                                                        <input
                                                            type="text"
                                                            value={user.accountNumber}
                                                            onKeyDown={preventInvalidKeyInput}
                                                            onChange={(e) => {
                                                                const sanitizedValue = sanitizeNumberInput(e.target.value);
                                                                setUser((prev) => ({
                                                                    ...prev,
                                                                    accountNumber: sanitizedValue,
                                                                }));
                                                            }}
                                                            placeholder="0"
                                                            className="bg-transparent text-sm outline-none text-white"
                                                        />
                                                    </div>
                                                </div>

                                                <p className="mb-3 text-sm font-bold text-white">
                                                    {t("EnterYourBankAccountName")}:
                                                </p>

                                                <div className="p-3 mb-3 bg-secondary rounded-lg flex-between">
                                                    <div className="flex flex-col">
                                                        <input
                                                            type="text"
                                                            value={user.accountName}
                                                            onChange={(e) => {
                                                                setUser((prev) => ({
                                                                    ...prev,
                                                                    accountName: e.target.value,
                                                                }));
                                                            }}
                                                            placeholder={t("EnterYourBankAccountName")}
                                                            className="bg-transparent text-sm outline-none text-white"
                                                        />
                                                    </div>
                                                </div>

                                                <p className="text-xs italic text-white">
                                                    {t("WithdrawalBankDetailsDisclaimer1")}
                                                </p>
                                                <p className="mb-2 text-xs italic text-white">
                                                    {t("WithdrawalBankDetailsDisclaimer2")}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        <button
                            className={`fixed bottom-[60px] md:bottom-0 left-0 right-0 container-lg h-[60px] text-center btn-gradient transition-all duration-300 ease-in-out ${
                                !error.message && user.method && user.accountName && user.accountNumber && user.selected
                                    ? "opacity-100 translate-y-0 scale-100"
                                    : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                            }`}
                            onClick={() => {
                                handleUpdateUserBank();
                            }}
                        >
                            <span className="text-sm font-bold">
                                {t("submit")}
                            </span>
                        </button>

                        <button
                            className={`fixed bottom-[60px] md:bottom-0 left-0 right-0 container-lg h-[60px] text-center btn-gradient transition-all duration-300 ease-in-out ${
                                !error.message && user.method && bank.selected && bank.selected.account_holder && bank.selected.account_no && withdrawal.amount 
                                    ? "opacity-100 translate-y-0 scale-100"
                                    : "opacity-0 translate-y-4 scale-95 pointer-events-none"
                            }`}
                            onClick={() => {
                                handleSubmit();
                            }}
                        >
                            <span className="text-sm font-bold">
                                {t("submit")}
                            </span>
                        </button>
                    </>
                )}
            </div>

            <FloatingMenu />
        </section>
    );
};

export default PaymentGatewayWithdraw;
